/**
 * Admin — Demo Leads.
 *
 * Route: /admin/demo-leads (RestroPulse operator only).
 *
 * Management/view layer over the existing `demo_access_leads`
 * collection. Public-demo visitors, guests, and managers are
 * server-side rejected — the UI is only surfaced here for admin /
 * super-admin roles inside the RestroPulse operator tenant.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, Download, RefreshCw, Search as SearchIcon, X, Phone,
  ShieldCheck, ShieldOff,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const STATUS_OPTIONS = ['new', 'contacted', 'interested', 'not_interested', 'converted'];
const STATUS_LABEL = {
  new: 'New', contacted: 'Contacted', interested: 'Interested',
  not_interested: 'Not interested', converted: 'Converted',
};
const STATUS_STYLES = {
  new:            'bg-blue-100 text-blue-800 border-blue-200',
  contacted:      'bg-amber-100 text-amber-800 border-amber-200',
  interested:     'bg-indigo-100 text-indigo-800 border-indigo-200',
  not_interested: 'bg-gray-100 text-gray-700 border-gray-200',
  converted:      'bg-emerald-100 text-emerald-800 border-emerald-200',
};

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.valueOf())) return v;
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
};

export default function AdminDemoLeads() {
  const { API, getHeaders } = useAuth();
  const [rows, setRows]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [status, setStatus]       = useState('__all__');
  const [persona, setPersona]     = useState('__all__');
  const [consent, setConsent]     = useState('__all__');
  const [source, setSource]       = useState('');
  const [campaign, setCampaign]   = useState('');
  const [dateFrom, setDateFrom]   = useState('');
  const [dateTo, setDateTo]       = useState('');
  const [search, setSearch]       = useState('');
  const [selected, setSelected]   = useState(null);

  const params = useMemo(() => {
    const p = {};
    if (status   !== '__all__') p.status = status;
    if (persona  !== '__all__') p.persona = persona;
    if (consent  !== '__all__') p.consent = consent;
    if (source)   p.source    = source;
    if (campaign) p.campaign  = campaign;
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo)   p.date_to   = dateTo;
    if (search)   p.search    = search;
    return p;
  }, [status, persona, consent, source, campaign, dateFrom, dateTo, search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/demo-leads`, {
        headers: getHeaders(), params: { ...params, limit: 500 },
      });
      setRows(res.data?.leads || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      const status = e.response?.status;
      if (status === 403) {
        toast.error('You do not have access to Demo Leads.');
      } else {
        toast.error('Failed to load demo leads.');
      }
      setRows([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [API, getHeaders, params]);

  useEffect(() => { load(); }, [load]);

  const patchLead = async (leadId, patch) => {
    try {
      const res = await axios.patch(
        `${API}/admin/demo-leads/${encodeURIComponent(leadId)}`,
        patch,
        { headers: getHeaders() },
      );
      setRows((prev) => prev.map((r) => r.id === leadId ? res.data : r));
      if (selected?.id === leadId) setSelected(res.data);
      toast.success('Lead updated');
      return res.data;
    } catch {
      toast.error('Could not update lead');
    }
  };

  const downloadCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/demo-leads.csv`, {
        headers: getHeaders(), params, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `demo_leads_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('CSV export failed');
    }
  };

  const resetFilters = () => {
    setStatus('__all__'); setPersona('__all__'); setConsent('__all__');
    setSource(''); setCampaign(''); setDateFrom(''); setDateTo('');
    setSearch('');
  };

  return (
    <div className="min-h-screen bg-[#FAFBF8]" data-testid="admin-demo-leads-page">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1 text-[10px] uppercase tracking-[0.22em] text-[#5C6B64] font-semibold">
              <Users className="w-3.5 h-3.5" strokeWidth={1.75} /> RestroPulse · Sales
            </div>
            <h1 className="text-[28px] sm:text-[32px] font-semibold text-[#1A2421] tracking-tight leading-tight">
              Demo Leads
            </h1>
            <p className="text-sm text-slate-600 mt-1.5">
              People who requested access to the public demo. Same email = one row.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={load} data-testid="demo-leads-refresh"
                    className="border-[#143E30]/40 text-[#143E30] hover:bg-[#E7EEEB]">
              <RefreshCw className="w-4 h-4 mr-1.5" strokeWidth={1.75} /> Refresh
            </Button>
            <Button onClick={downloadCsv} data-testid="demo-leads-export"
                    className="bg-[#143E30] hover:bg-[#1B5A45] text-white">
              <Download className="w-4 h-4 mr-1.5" strokeWidth={1.75} /> Export CSV
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-5 border-[#E2E8DF]">
          <CardContent className="p-4 grid gap-3 md:grid-cols-6">
            <div className="md:col-span-2">
              <label className="text-[11px] uppercase tracking-wider text-slate-500">
                Search email / mobile
              </label>
              <div className="relative">
                <SearchIcon className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" strokeWidth={1.75}/>
                <Input value={search} onChange={(e)=>setSearch(e.target.value)}
                       placeholder="you@company.com or +91…"
                       data-testid="demo-leads-search"
                       className="pl-8 h-9" />
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500">Status</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-9" data-testid="demo-leads-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All</SelectItem>
                  {STATUS_OPTIONS.map(s => (
                    <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500">Persona</label>
              <Select value={persona} onValueChange={setPersona}>
                <SelectTrigger className="h-9" data-testid="demo-leads-persona-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All</SelectItem>
                  <SelectItem value="owner">Owner / Admin</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500">Consent</label>
              <Select value={consent} onValueChange={setConsent}>
                <SelectTrigger className="h-9" data-testid="demo-leads-consent-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All</SelectItem>
                  <SelectItem value="yes">Yes — follow-up permitted</SelectItem>
                  <SelectItem value="no">No — do not contact</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500">Source</label>
              <Input value={source} onChange={(e)=>setSource(e.target.value)}
                     placeholder="e.g. google" className="h-9"
                     data-testid="demo-leads-source-filter"/>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500">Campaign</label>
              <Input value={campaign} onChange={(e)=>setCampaign(e.target.value)}
                     placeholder="e.g. launch" className="h-9"
                     data-testid="demo-leads-campaign-filter"/>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500">From</label>
              <Input type="date" value={dateFrom} onChange={(e)=>setDateFrom(e.target.value)}
                     className="h-9" data-testid="demo-leads-date-from"/>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500">To</label>
              <Input type="date" value={dateTo} onChange={(e)=>setDateTo(e.target.value)}
                     className="h-9" data-testid="demo-leads-date-to"/>
            </div>
            <div className="md:col-span-2 flex items-end justify-end">
              <Button variant="ghost" onClick={resetFilters}
                      className="text-slate-500" data-testid="demo-leads-reset">
                <X className="w-3.5 h-3.5 mr-1" strokeWidth={1.75}/> Clear filters
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="border-[#E2E8DF]">
          <CardContent className="p-0">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E2E8DF]">
              <div className="text-sm text-slate-600">
                {loading ? 'Loading…' : `${rows.length} of ${total} lead${total === 1 ? '' : 's'}`}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-[1100px] w-full text-sm" data-testid="demo-leads-table">
                <thead className="bg-[#F4F7F5] text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2.5">Email</th>
                    <th className="text-left px-4 py-2.5">Mobile</th>
                    <th className="text-left px-4 py-2.5">Consent</th>
                    <th className="text-left px-4 py-2.5">Persona</th>
                    <th className="text-left px-4 py-2.5">Source</th>
                    <th className="text-left px-4 py-2.5">Campaign</th>
                    <th className="text-left px-4 py-2.5">First</th>
                    <th className="text-left px-4 py-2.5">Last</th>
                    <th className="text-right px-4 py-2.5">Visits</th>
                    <th className="text-left px-4 py-2.5">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && !loading && (
                    <tr>
                      <td colSpan={10} className="px-4 py-10 text-center text-slate-500">
                        No leads match your filters yet.
                      </td>
                    </tr>
                  )}
                  {rows.map((r) => (
                    <tr key={r.id}
                        onClick={() => setSelected(r)}
                        data-testid={`demo-lead-row-${r.id}`}
                        className="border-t border-[#E2E8DF] hover:bg-[#F4F7F5] cursor-pointer">
                      <td className="px-4 py-2.5 font-medium text-[#1A2421]">{r.email}</td>
                      <td className="px-4 py-2.5 text-slate-600">{r.mobile || '—'}</td>
                      <td className="px-4 py-2.5">
                        {r.followup_consent ? (
                          <span className="inline-flex items-center gap-1 text-emerald-800 text-xs font-semibold">
                            <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2}/>
                            YES — Follow-up permitted
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-slate-500 text-xs font-semibold">
                            <ShieldOff className="w-3.5 h-3.5" strokeWidth={2}/>
                            NO — Do not contact
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 capitalize text-slate-600">{r.persona || '—'}</td>
                      <td className="px-4 py-2.5 text-slate-600">{r.source || '—'}</td>
                      <td className="px-4 py-2.5 text-slate-600">{r.campaign || '—'}</td>
                      <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{fmtDate(r.first_access_at)}</td>
                      <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{fmtDate(r.last_access_at)}</td>
                      <td className="px-4 py-2.5 text-right font-mono">{r.access_count}</td>
                      <td className="px-4 py-2.5">
                        <Badge className={`${STATUS_STYLES[r.status] || STATUS_STYLES.new} border`}>
                          {STATUS_LABEL[r.status] || 'New'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {selected && (
          <DetailDrawer lead={selected} onClose={() => setSelected(null)}
                         onSave={patchLead} />
        )}
      </div>
    </div>
  );
}


/* ── Detail drawer ─────────────────────────────────────────────────── */
function DetailDrawer({ lead, onClose, onSave }) {
  const [status, setStatus] = useState(lead.status || 'new');
  const [notes, setNotes]   = useState(lead.notes || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setStatus(lead.status || 'new');
    setNotes(lead.notes || '');
  }, [lead.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = status !== (lead.status || 'new') || notes !== (lead.notes || '');

  const save = async () => {
    setSaving(true);
    await onSave(lead.id, { status, notes });
    setSaving(false);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose}
           data-testid="demo-lead-drawer-scrim" />
      <aside data-testid="demo-lead-drawer"
             className="fixed top-0 right-0 h-full w-full max-w-[520px] bg-white
                        z-50 shadow-2xl border-l border-[#E2E8DF] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E2E8DF] sticky top-0 bg-white">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C6B64] font-semibold">Lead</div>
            <h2 className="text-lg font-semibold text-[#1A2421] tracking-tight">{lead.email}</h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="demo-lead-drawer-close">
            <X className="w-4 h-4" strokeWidth={1.75}/>
          </Button>
        </div>

        <div className="p-6 space-y-6 text-sm">
          <Section title="Contact">
            <Row label="Email" value={lead.email}/>
            <Row label="Mobile" value={
              lead.mobile ? (
                <a href={`tel:${lead.mobile}`} className="text-[#143E30] hover:underline inline-flex items-center gap-1">
                  <Phone className="w-3.5 h-3.5" strokeWidth={2}/> {lead.mobile}
                </a>
              ) : '—'
            }/>
            <Row label="Consent (read-only)" value={
              lead.followup_consent ? (
                <span className="inline-flex items-center gap-1 text-emerald-800 font-semibold">
                  <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2}/>
                  YES — Follow-up permitted
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-slate-500 font-semibold">
                  <ShieldOff className="w-3.5 h-3.5" strokeWidth={2}/>
                  NO — Do not contact
                </span>
              )
            }/>
            {lead.followup_consent_at && (
              <Row label="Consent captured at" value={fmtDate(lead.followup_consent_at)}/>
            )}
          </Section>

          <Section title="Demo behaviour">
            <Row label="First access" value={fmtDate(lead.first_access_at)}/>
            <Row label="Last access"  value={fmtDate(lead.last_access_at)}/>
            <Row label="Access count" value={String(lead.access_count || 0)}/>
            <Row label="Last persona" value={lead.persona || '—'}/>
          </Section>

          <Section title="Attribution">
            <Row label="utm_source"   value={lead.source   || '—'}/>
            <Row label="utm_medium"   value={lead.medium   || '—'}/>
            <Row label="utm_campaign" value={lead.campaign || '—'}/>
            <Row label="utm_content"  value={lead.content  || '—'}/>
            <Row label="utm_term"     value={lead.term     || '—'}/>
            <Row label="Referrer"     value={lead.referrer || '—'}/>
          </Section>

          <Section title="Sales">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 block">
                Status
              </label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-9" data-testid="demo-lead-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(s => (
                    <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="mt-3">
              <label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 block">
                Notes
              </label>
              <Textarea value={notes} onChange={(e)=>setNotes(e.target.value)}
                        rows={5} placeholder="Add sales notes (visible to admins only)."
                        data-testid="demo-lead-notes"/>
            </div>
            {lead.contacted_at && (
              <Row label="Last contacted" value={fmtDate(lead.contacted_at)}/>
            )}
            {lead.updated_by && (
              <Row label="Last edited by" value={`${lead.updated_by} · ${fmtDate(lead.updated_at)}`}/>
            )}

            <div className="mt-4 flex items-center justify-end gap-2">
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button onClick={save} disabled={!dirty || saving}
                      data-testid="demo-lead-save"
                      className="bg-[#143E30] hover:bg-[#1B5A45] text-white">
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </Section>
        </div>
      </aside>
    </>
  );
}

const Section = ({ title, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C6B64] font-semibold mb-2">{title}</div>
    <div className="space-y-1.5">{children}</div>
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 text-sm">
    <span className="text-slate-500 min-w-[140px]">{label}</span>
    <span className="text-[#1A2421] text-right">{value}</span>
  </div>
);
